import { useEffect, useRef, useState, useCallback } from "react";
import { api, resolveMediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Trash2 } from "lucide-react";

export const PhotosTab = () => {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/photos").then((res) => setPhotos(res.data)).catch(() => toast.error("Could not load photos"));
  }, []);

  useEffect(load, [load]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isJpeg = f.type === "image/jpeg" || /\.(jpe?g)$/i.test(f.name);
    if (!isJpeg) {
      toast.error("JPEG only — please choose a .jpg or .jpeg file.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose a JPEG photo first.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("caption", caption);
      await api.post("/admin/photos", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo published to the gallery");
      setFile(null);
      setCaption("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Photo removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="photos-tab">
      <h2 className="font-serif-display text-2xl font-semibold text-[#0A192F] mb-2">Gallery Photos</h2>
      <p className="text-[#4A5568] text-sm mb-6">JPEG format only (.jpg / .jpeg), up to 10MB. Photos appear instantly in the guest gallery.</p>

      <div className="bg-[#0A192F] rounded-2xl p-6 sm:p-8 mb-8" data-testid="photo-upload-panel">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="text-xs text-[#F8F6F0]/50 block mb-1.5">JPEG file *</label>
            <input ref={inputRef} type="file" accept=".jpg,.jpeg,image/jpeg" onChange={onPick} className="w-full text-sm text-[#F8F6F0]/70 file:mr-4 file:rounded-full file:border-0 file:bg-[#C5A059] file:text-[#0A192F] file:font-semibold file:px-5 file:py-2.5 file:text-xs hover:file:bg-[#D4AF37] file:cursor-pointer" data-testid="photo-file-input" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-[#F8F6F0]/50 block mb-1.5">Caption</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Sunset from the veranda" className="bg-white border border-[#0D1B2A]/20 focus:border-[#C5A059] rounded-xl px-4 py-2.5 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full text-sm" data-testid="photo-caption-input" />
          </div>
          <button onClick={upload} disabled={uploading} className="flex items-center justify-center gap-2 bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full px-7 py-3 font-semibold text-sm transition-all disabled:opacity-60 shrink-0" data-testid="photo-upload-button">
            <UploadCloud size={16} /> {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {file && (
          <div className="mt-4 flex items-center gap-4">
            <img src={URL.createObjectURL(file)} alt="Preview" className="w-20 h-20 object-cover rounded-lg border border-[#C5A059]/40" data-testid="photo-preview" />
            <p className="text-[#F8F6F0]/60 text-xs">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="group relative rounded-xl overflow-hidden bg-white border border-[#0D1B2A]/10" data-testid={`photo-card-${p.id}`}>
            <img src={resolveMediaUrl(p.url)} alt={p.caption || "Gallery photo"} className="w-full h-44 object-cover" />
            {p.caption && <p className="text-xs text-[#4A5568] px-3 py-2 truncate">{p.caption}</p>}
            <button onClick={() => remove(p.id)} className="absolute top-2 right-2 bg-[#0A192F]/80 text-red-300 hover:text-red-500 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-delete-${p.id}`} aria-label="Delete photo">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
